import React, { useEffect, useState, useCallback } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, LabelList } from 'recharts';
import { IoDocuments } from "react-icons/io5";
import { FaUserClock, FaUserCheck, FaUserTimes, FaChartBar, FaChartPie } from "react-icons/fa";

import '../../../css/UserAdmin/AnalyticsSection.css';
import CardModule from '../CardModule';

const EmptyChartState = ({ message }) => (
  <div className="empty-chart-state">
    <p>{message}</p>
  </div>
);

const AdmissionAnalytics = ({ hasAnalyticsAccess }) => {
  const [academicYears, setAcademicYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState('');
  const [yearsLoading, setYearsLoading] = useState(true);
  const [yearsError, setYearsError] = useState(null);

  const [loading, setLoading] = useState(false);
  const [admissionStats, setAdmissionStats] = useState({ all: 0, pending: 0, approved: 0, rejected: 0 });
  const [yearlyTrendData, setYearlyTrendData] = useState([]);
  const [strandDistributionData, setStrandDistributionData] = useState([]);

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8'];

  useEffect(() => {
    const fetchAcademicYears = async () => {
      setYearsLoading(true);
      setYearsError(null);
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/years?includeArchived=true&includeInactive=true`);
        if (!response.ok) throw new Error('Failed to fetch academic years');
        const data = await response.json();
        const years = data.data || [];
        setAcademicYears(years);

        const activeYear = years.find(year => year.status === 'Active');
        if (activeYear) {
          setSelectedYear(activeYear.Year);
        } else if (years.length > 0) {
          setSelectedYear(years[0].Year);
        }
      } catch (error) {
        console.error("Error fetching academic years:", error);
        setYearsError('Could not load academic years.');
      } finally {
        setYearsLoading(false);
      }
    };
    fetchAcademicYears();
  }, []);

  useEffect(() => {
    const fetchYearlyTrend = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/applicants/yearly-trend`);
        if (!response.ok) {
          throw new Error('Failed to fetch yearly trend data');
        }
        const data = await response.json();
        setYearlyTrendData(data.data || []);
      } catch (error) {
        console.error("Error fetching yearly trend:", error);
        setYearlyTrendData([]);
      }
    };

    if (hasAnalyticsAccess) {
      fetchYearlyTrend();
    }
  }, [hasAnalyticsAccess]);

  const fetchAnalyticsData = useCallback(async (year) => {
    if (!year) return;

    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/applicants/all-applicants?academicYear=${encodeURIComponent(year)}&limit=1`);
      if (!response.ok) {
        throw new Error(`Failed to fetch applicant count for year ${year}`);
      }
      const data = await response.json();

      const counts = data.pagination?.counts || { all: 0, pending: 0, approved: 0, rejected: 0 };

      setAdmissionStats({
        all: counts.all,
        pending: counts.pending,
        approved: counts.approved,
        rejected: counts.rejected,
      });

      const strandDataFromAPI = counts.strandDistribution || [];
      setStrandDistributionData(strandDataFromAPI);

    } catch (error) {
      console.error("Error fetching admission analytics:", error);
      setAdmissionStats({ all: 0, pending: 0, approved: 0, rejected: 0 });
      setStrandDistributionData([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (hasAnalyticsAccess && selectedYear) {
      fetchAnalyticsData(selectedYear);
    }
  }, [selectedYear, hasAnalyticsAccess, fetchAnalyticsData]);


  if (!hasAnalyticsAccess) {
    return null;
  }
  
  return (
    // The unique class 'admission-analytics' is added here to target its children with CSS
    <div className="analytics-section admission-analytics">
      <div className="analytics-header">
        <h3 style={{ margin: 0, color: '#2c3e50', fontSize: '16px' }}>Admission Analytics</h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <label htmlFor="academic-year-selector">Academic Year:</label>
          <select
            id="academic-year-selector"
            className="timeframe-selector"
            value={selectedYear}
            onChange={(e) => setSelectedYear(e.target.value)}
            disabled={yearsLoading || loading}
          >
            {yearsLoading ? (
              <option>Loading years...</option>
            ) : yearsError ? (
              <option>{yearsError}</option>
            ) : (
              academicYears.map(year => (
                <option key={year._id} value={year.Year}>
                  {year.Year} {year.status === 'Active' && '(Active)'}
                </option>
              ))
            )}
          </select>
        </div>
      </div>
      {loading ? (
        <div className="loading-analytics-indicator">Loading admission data...</div>
      ) : (
        <>
          <div className="card-container" style={{ marginBottom: '2rem' }}>
            <CardModule
              title="All"
              description="All applications (active & archived)"
              statistics={admissionStats.all.toString()}
              icon={IoDocuments}
              path={'/user-admin/applications'}
            />
            <CardModule
              title="Pending"
              description="Active applications (not archived)"
              statistics={admissionStats.pending.toString()}
              icon={FaUserClock}
              path={'/user-admin/applications'}
            />
            <CardModule
              title="Approved"
              description="Approved & archived applications"
              statistics={admissionStats.approved.toString()}
              icon={FaUserCheck}
              path={'/user-admin/applications'}
            />
            <CardModule
              title="Rejected"
              description="Rejected & archived applications"
              statistics={admissionStats.rejected.toString()}
              icon={FaUserTimes}
              path={'/user-admin/applications'}
            />
          </div>

          <div className="charts-row-container">
            <div className="chart-card">
              <h3 className="chart-title"><FaChartPie /> Applicants Per Strand</h3>
              <p className="chart-description">Distribution by Strand (All Applicants)</p>
              {strandDistributionData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={strandDistributionData}
                      cx="50%"
                      cy="50%"
                      labelLine={true}
                      label={({ name, value, percent }) => `${name}: ${value} (${(percent * 100).toFixed(0)}%)`}
                      outerRadius={85}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {strandDistributionData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value, name) => [value, name]} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <EmptyChartState message={`No applicant data found for ${selectedYear}.`} />
              )}
            </div>

            <div className="chart-card">
              <h3 className="chart-title"><FaChartBar /> No. of Applicants Per Year</h3>
              <p className="chart-description">Application Trend Analysis</p>
              {yearlyTrendData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={yearlyTrendData} margin={{ top: 20, right: 20, left: -10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="year" />
                    <YAxis allowDecimals={false} />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="applicants" name="Total Applicants" fill={COLORS[0]}>
                      <LabelList dataKey="applicants" position="top" style={{ fill: '#34495e', fontSize: '14px' }} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <EmptyChartState message="No application trend data available." />
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AdmissionAnalytics;